//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import cx from 'classnames';

import ShowWhiteSpaceChars from 'components/ShowWhiteSpaceChars';
import { tNamespaced } from 'utils/i18nUtil';
import { parseFuzzyValue } from '../FuzzySelect/utils/fuzzyValueHelpers';

import './ConditionReadOnly.less';

const tsf = tNamespaced('SimpleFilter');

interface ConditionReadOnlyProps {
  left?: string;
  operator?: string;
  right?: string | React.ReactNode;
  className?: string;
  /* try to let the backend set our label before we use local translations */
  operatorLabel?: string;
  /* whether the fuzzy number is a typo count (Max N typos) vs a percentage */
  hasMinMaxTypoConfig?: boolean;
}

const getFuzzySuffix = (percentage: number | null, hasMinMaxTypoConfig?: boolean): string => {
  if (percentage === null) {
    return '';
  }

  if (hasMinMaxTypoConfig) {
    const typoText = percentage === 1 ? 'typo' : 'typos';
    return ` (max ${percentage} ${typoText})`;
  }

  return ` (${percentage}%)`;
};

const getOperatorDisplay = (
  operator: string | undefined,
  operatorLabel: string | undefined,
  hasMinMaxTypoConfig?: boolean
): string => {
  // Parse potential fuzzy operator (e.g., "resembles:70" -> "resembles", 70)
  const { value: baseOperator, percentage } = parseFuzzyValue(operator ?? '');

  let baseText: string;
  if (operatorLabel) {
    baseText = operatorLabel.toLowerCase();
  } else if (!operator) {
    return '';
  } else {
    baseText = tsf(baseOperator).toLowerCase();
  }

  return baseText + getFuzzySuffix(percentage, hasMinMaxTypoConfig);
};

const ConditionReadOnly = ({
  left,
  operator,
  operatorLabel,
  right,
  className,
  hasMinMaxTypoConfig,
}: ConditionReadOnlyProps) => {
  // Show empty state if all values are missing
  if (!left && !operator && !right) {
    return <span>{tsf('empty_filter')}</span>;
  }

  const operatorText = getOperatorDisplay(operator, operatorLabel, hasMinMaxTypoConfig);

  return (
    <span className={cx('synri-condition-readonly', className)}>
      <span className="synri-left-value">{left}</span>
      <span> {operatorText} </span>
      <span className="synri-right-value">
        <ShowWhiteSpaceChars>{right}</ShowWhiteSpaceChars>
      </span>
    </span>
  );
};

export default ConditionReadOnly;
